import React from "react";
import { Link } from "react-router-dom";
import logo from "../assets/images/brand-blanc.png";
import { useTranslation } from "react-i18next";
import { CiLinkedin } from "react-icons/ci";
import { FaXTwitter } from "react-icons/fa6";
import { CiYoutube } from "react-icons/ci";

interface FooterProps {
  menu: MenuItem[];
}

interface MenuItem {
  name: String;
  to: string;
}

function Footer({ menu }: FooterProps) {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation("global");

  return (
    <footer className="bg-gradient-to-r from-blue-800 to-blue-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand & Description */}
          <div className="col-span-1 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img src={logo} alt="ISOeVAL" className="w-32" />
            </Link>
            <p className="text-blue-200 mb-4">
              {t("footer.company-description", "Évaluez et améliorez la maturité de votre entreprise avec notre plateforme spécialisée en conformité SSI, QSE, RSE et RGPD.")}
            </p>
            <div className="flex space-x-4 mt-4">
              <a
                href="https://www.linkedin.com/company/isoeval/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors duration-300"
                aria-label={t("footer.linkedin-aria", "LinkedIn")}
              >
                <CiLinkedin size={20} />
              </a>
              <a
                href="https://x.com/ISOeVAL_"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors duration-300"
                aria-label={t("footer.twitter-aria", "Twitter/X")}
              >
                <FaXTwitter size={20} />
              </a>
              <a
                href="https://www.youtube.com/@ISOeVAL"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors duration-300"
                aria-label={t("footer.youtube-aria", "YouTube")}
              >
                <CiYoutube size={20} />
              </a>
            </div>
          </div>
          
          {/* Navigation Links */}
          <div className="col-span-1">
            <h3 className="text-lg font-bold mb-4 pb-2 border-b border-blue-700">{t("footer.menu-heading", "Menu")}</h3>
            <ul className="space-y-2">
              {menu.map((item, index) => (
                <li key={index}>
                  <a
                    href={item.to}
                    className="text-blue-200 hover:text-white transition-colors duration-200 group inline-block"
                  >
                    {item.name}
                    <span className="block h-0.5 w-0 group-hover:w-full transition-all duration-300 bg-blue-400"></span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Legal Links */}
          <div className="col-span-1">
            <h3 className="text-lg font-bold mb-4 pb-2 border-b border-blue-700">{t("footer.information-heading", "Informations")}</h3>
            <ul className="space-y-2">
              <li>
                  <a
                  href="support"
                  className="text-blue-200 hover:text-white transition-colors duration-200 group inline-block"
                >
                  {t("footer.support")}
                  <span className="block h-0.5 w-0 group-hover:w-full transition-all duration-300 bg-blue-400"></span>
                </a>
              </li>
              <li>
                  <a
                  href="privacy"
                  className="text-blue-200 hover:text-white transition-colors duration-200 group inline-block"
                >
                  {t("footer.privacy")}
                  <span className="block h-0.5 w-0 group-hover:w-full transition-all duration-300 bg-blue-400"></span>
                </a>
              </li>
              <li>
                  <a
                  href="conditions"
                  className="text-blue-200 hover:text-white transition-colors duration-200 group inline-block"
                >
                  {t("conditions.title")}
                  <span className="block h-0.5 w-0 group-hover:w-full transition-all duration-300 bg-blue-400"></span>
                </a>
              </li>
              <li>
                <a
                href="faq"
                  className="text-blue-200 hover:text-white transition-colors duration-200 group inline-block"
                >
                  {t("FAQ.title")}
                  <span className="block h-0.5 w-0 group-hover:w-full transition-all duration-300 bg-blue-400"></span>
                </a>
              </li>
            </ul>
          </div>

          {/* Contact & Newsletter */}
          <div className="col-span-1">
            <h3 className="text-lg font-bold mb-4 pb-2 border-b border-blue-700">Contact</h3>
            <div className="bg-blue-900/30 p-4 rounded-lg">
              <p className="text-blue-200 mb-4">{t("footer.contact-description", "Besoin d'aide ou d'informations supplémentaires?")}</p>
              <a
                href="contact"
                className="inline-block bg-blue-600 hover:bg-blue-700 py-2 px-4 rounded-full text-white font-medium transition-colors duration-300"
              >
                {t("contact.contact")}
              </a>
            </div>
            {/* <div className="mt-4">
              <p className="text-blue-200">Restez informé des dernières actualités</p>
              <div className="flex mt-2">
                <input
                  type="email"
                  placeholder={t("footer.email")}
                  className="bg-blue-900/30 text-white rounded-l-full px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-r-full px-4 font-medium transition-colors duration-300">
                  {t("footer.send")}
                </button>
              </div>
            </div> */}
          </div>
        </div>
      </div>
      
      {/* Footer Bottom */}
      <div className="border-t border-blue-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <div className="text-blue-300 text-sm">
              &copy; {currentYear} ISOeVAL. {t("footer.copyright")}
            </div>
            {/* <div className="flex flex-wrap gap-4 mt-2 md:mt-0">
              <a href="/sitemap" className="text-blue-300 hover:text-white text-sm">
                Sitemap
              </a>
              <a href="/accessibility" className="text-blue-300 hover:text-white text-sm">
                Accessibilité
              </a>
              <a href="/mentions-legales" className="text-blue-300 hover:text-white text-sm">
                Mentions légales
              </a>
            </div> */}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;